import React from 'react';
import { useTranslation } from 'react-i18next';

import './index.less';

import IbIcon, { IbIconName } from '../../common/IbIcon';
import IbTypography from '../../common/IbTypography';
import { GuardrailEventData, isGuardrailCheckBlocked, isGuardrailCheckWarning } from '../guardrailChecks';

const MAIN_CLASS_NAME = 'guardrail-checks-event';
const SELECTED_CLASS_NAME = `${MAIN_CLASS_NAME}_selected`;
const ERROR_CLASS_NAME = `${MAIN_CLASS_NAME}_error`;
const WARNING_CLASS_NAME = `${MAIN_CLASS_NAME}_warning`;
const DEFAULT_SIZE_CLASS_NAME = `${MAIN_CLASS_NAME}_size-default`;
const SMALL_SIZE_CLASS_NAME = `${MAIN_CLASS_NAME}_size-small`;
const ICON_CONTAINER_CLASS_NAME = `${MAIN_CLASS_NAME}__icon-container`;
const STATUS_CLASS_NAME = `${MAIN_CLASS_NAME}__status`;

export enum GuardrailChecksEventSize {
  DEFAULT = 'default',
  SMALL = 'small',
}

const iconSizeMap = {
  [GuardrailChecksEventSize.DEFAULT]: 20,
  [GuardrailChecksEventSize.SMALL]: 16,
};

const paragraphTypeMap = {
  [GuardrailChecksEventSize.DEFAULT]: 'normal' as const,
  [GuardrailChecksEventSize.SMALL]: 'secondary' as const,
};

export interface IGuardrailChecksEventProps {
  guardrailData: GuardrailEventData;
  isSelected?: boolean;
  size?: GuardrailChecksEventSize;
  onSelect?: () => void;
}

const GuardrailChecksEvent: React.FC<IGuardrailChecksEventProps> = ({
  guardrailData,
  isSelected = false,
  size = GuardrailChecksEventSize.DEFAULT,
  onSelect,
}) => {
  const { t } = useTranslation();
  const guardrailChecks = guardrailData.checks || [];
  const hasError = guardrailChecks.some(isGuardrailCheckBlocked);
  const hasWarning = !hasError && guardrailChecks.some(isGuardrailCheckWarning);

  const classes = [MAIN_CLASS_NAME];
  if (hasError) {
    classes.push(ERROR_CLASS_NAME);
  }
  if (hasWarning) {
    classes.push(WARNING_CLASS_NAME);
  }
  if (isSelected) {
    classes.push(SELECTED_CLASS_NAME);
  }
  if (size === GuardrailChecksEventSize.DEFAULT) {
    classes.push(DEFAULT_SIZE_CLASS_NAME);
  }
  if (size === GuardrailChecksEventSize.SMALL) {
    classes.push(SMALL_SIZE_CLASS_NAME);
  }

  const onClick = () => onSelect?.();
  const statusIcon: IbIconName = hasError || hasWarning ? 'attention' : 'check-one';

  return (
    <div className={classes.join(' ')} onClick={onClick}>
      <div className={ICON_CONTAINER_CLASS_NAME}>
        <IbIcon iconName="detection" size={iconSizeMap[size]} />
      </div>
      <IbTypography.Paragraph type={paragraphTypeMap[size]}>
        {t('Agent: Guardrail checks event: Title')}
      </IbTypography.Paragraph>
      <IbIcon className={STATUS_CLASS_NAME} iconName={statusIcon} size={iconSizeMap[size]} />
    </div>
  );
};

export default GuardrailChecksEvent;
